// web-signals.ts — faithful JS port of src-tauri/src/signals.rs::compute for the WEB surface.
// On native the derived signals are computed in Rust over SQLite; in a browser there is no Rust,
// and the old web shell returned an EMPTY signals object — which made the two flagship AI features
// (Weekly reflection + Ask your moods) answer "log a few moods first" even with a full log.
// This port computes the SAME aggregates from the WebStore rows, so the web surface reflects for real.
//
// Same ceilings as the Rust source: OBSERVATIONAL ONLY (facts: averages, deltas, std-devs) and
// LOCAL-FIRST (raw notes never enter the signals object — counts and averages only).
// All date math is UTC-anchored (like chrono::NaiveDate: calendar days, no DST wobble).

import type {
  MoodSignals, WindowStats, WindowShift, TagCorrelation, DowStat, Volatility, Trajectory, WeekPoint,
} from "./store";

export interface SignalEntry { logged_on: string; score: number; tags: string[] }

const HORIZON_DAYS = 56; // 8 weeks — day-of-week + tag + trajectory lens
const WINDOW_DAYS = 14;  // current-vs-prior comparison blocks
const DAY_MS = 86_400_000;

const round2 = (x: number): number => Math.round(x * 100) / 100;

function mean(xs: number[]): number | null {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
}

/** Population std-dev (we have the full set, not a sample) — mirrors signals.rs::std_dev. */
function stdDev(xs: number[]): number | null {
  if (xs.length < 2) return null;
  const m = xs.reduce((a, b) => a + b, 0) / xs.length;
  const variance = xs.reduce((s, v) => s + (v - m) ** 2, 0) / xs.length;
  return Math.sqrt(variance);
}

/** "YYYY-MM-DD…" → UTC Date at midnight, or null when malformed. */
function parseDay(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s ?? "");
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return Number.isNaN(d.getTime()) ? null : d;
}

const fmtDay = (d: Date): string =>
  `${d.getUTCFullYear().toString().padStart(4, "0")}-${(d.getUTCMonth() + 1).toString().padStart(2, "0")}-${d.getUTCDate().toString().padStart(2, "0")}`;

const addDays = (d: Date, n: number): Date => new Date(d.getTime() + n * DAY_MS);

/** ISO-8601 week key "YYYY-Www" — mirrors chrono's iso_week(). */
function isoWeekKey(d: Date): string {
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = (t.getUTCDay() + 6) % 7; // 0 = Mon
  t.setUTCDate(t.getUTCDate() - dayNum + 3); // the Thursday of this ISO week
  const isoYear = t.getUTCFullYear();
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const week = 1 + Math.round(((t.getTime() - jan4.getTime()) / DAY_MS - 3 + ((jan4.getUTCDay() + 6) % 7)) / 7);
  return `${isoYear}-W${String(week).padStart(2, "0")}`;
}

function windowStats(label: string, start: Date, end: Date, entries: SignalEntry[]): WindowStats {
  const scores = entries.map((e) => e.score);
  const days = new Set(entries.map((e) => e.logged_on.slice(0, 10)));
  const avg = mean(scores);
  return {
    label,
    start: fmtDay(start),
    end: fmtDay(end),
    entries: entries.length,
    days_logged: days.size,
    avg_score: avg === null ? null : round2(avg),
    min_score: scores.length ? Math.min(...scores) : null,
    max_score: scores.length ? Math.max(...scores) : null,
  };
}

/** Compute the full self-describing signals object from ALL local entries (port of signals.rs::compute). */
export function computeWebSignals(all: SignalEntry[], todayStr: string): MoodSignals {
  const today = parseDay(todayStr) ?? parseDay(new Date().toISOString())!;
  const dated: { d: Date; e: SignalEntry }[] = [];
  for (const e of all) {
    const d = parseDay(e.logged_on);
    if (d) dated.push({ d, e });
  }

  // ── Overall ──────────────────────────────────────────────────────────────
  const allScores = dated.map((x) => x.e.score);
  const first = dated.length ? new Date(Math.min(...dated.map((x) => x.d.getTime()))) : null;
  const last = dated.length ? new Date(Math.max(...dated.map((x) => x.d.getTime()))) : null;
  const overallAvg = mean(allScores);
  const overall: MoodSignals["overall"] = {
    total_entries: dated.length,
    days_span: first && last ? Math.round((last.getTime() - first.getTime()) / DAY_MS) + 1 : 0,
    avg_score: overallAvg === null ? null : round2(overallAvg),
    first_date: first ? fmtDay(first) : null,
    last_date: last ? fmtDay(last) : null,
    scale: "1=rough .. 5=great",
  };

  // ── Windows: current 14d vs prior 14d (anchored on today) ────────────────
  const curStart = addDays(today, -(WINDOW_DAYS - 1));
  const priorEnd = addDays(curStart, -1);
  const priorStart = addDays(priorEnd, -(WINDOW_DAYS - 1));
  const inRange = (d: Date, a: Date, b: Date) => d.getTime() >= a.getTime() && d.getTime() <= b.getTime();

  const curEntries = dated.filter((x) => inRange(x.d, curStart, today)).map((x) => x.e);
  const priorEntries = dated.filter((x) => inRange(x.d, priorStart, priorEnd)).map((x) => x.e);

  const current_window = windowStats("last 14 days", curStart, today, curEntries);
  const prior_window = priorEntries.length
    ? windowStats("the 14 days before that", priorStart, priorEnd, priorEntries)
    : null;

  let window_shift: WindowShift | null = null;
  const c = current_window.avg_score, p = prior_window?.avg_score ?? null;
  if (c !== null && p !== null && p !== undefined) {
    const delta = round2(c - p);
    window_shift = {
      current_avg: c,
      prior_avg: p,
      delta,
      direction: delta > 0.15 ? "up" : delta < -0.15 ? "down" : "flat",
      current_entries: current_window.entries,
      prior_entries: prior_window?.entries ?? 0,
    };
  }

  // ── Volatility: current vs prior window std-dev ──────────────────────────
  const curSd = stdDev(curEntries.map((e) => e.score));
  const priorSd = stdDev(priorEntries.map((e) => e.score));
  const cur_sd = curSd === null ? null : round2(curSd);
  const prior_sd = priorSd === null ? null : round2(priorSd);
  const volatility: Volatility = {
    current_std_dev: cur_sd,
    prior_std_dev: prior_sd,
    change:
      cur_sd !== null && prior_sd !== null
        ? (cur_sd - prior_sd < -0.2 ? "steadier" : cur_sd - prior_sd > 0.2 ? "more variable" : "similar")
        : null,
  };

  // ── Tag ↔ score correlation (over the 8-week horizon) ────────────────────
  const horizonStart = addDays(today, -(HORIZON_DAYS - 1));
  const horizon = dated.filter((x) => inRange(x.d, horizonStart, today));
  const horizonAvgRaw = mean(horizon.map((x) => x.e.score));
  const tag_correlations: TagCorrelation[] = [];
  if (horizonAvgRaw !== null) {
    const hAvg = round2(horizonAvgRaw);
    const acc = new Map<string, { sum: number; count: number }>();
    for (const { e } of horizon) {
      for (const t of Array.isArray(e.tags) ? e.tags : []) {
        const cur = acc.get(t) ?? { sum: 0, count: 0 };
        cur.sum += e.score;
        cur.count += 1;
        acc.set(t, cur);
      }
    }
    for (const [tag, { sum, count }] of acc) {
      if (count < 2) continue; // need ≥2 occurrences for the average to be meaningful
      const avg = round2(sum / count);
      const delta = round2(avg - hAvg);
      tag_correlations.push({
        tag,
        count,
        avg_with_tag: avg,
        overall_avg: hAvg,
        delta,
        direction: delta > 0.1 ? "higher" : delta < -0.1 ? "lower" : "same",
      });
    }
    tag_correlations.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
    tag_correlations.length = Math.min(tag_correlations.length, 8);
  }

  // ── Day-of-week pattern (over the 8-week horizon) ────────────────────────
  const dowLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const dowAcc: { sum: number; count: number }[] = dowLabels.map(() => ({ sum: 0, count: 0 }));
  for (const { d, e } of horizon) {
    const idx = (d.getUTCDay() + 6) % 7; // 0 = Mon
    dowAcc[idx].sum += e.score;
    dowAcc[idx].count += 1;
  }
  const day_of_week: DowStat[] = dowLabels.map((weekday, i) => ({
    weekday,
    count: dowAcc[i].count,
    avg_score: dowAcc[i].count > 0 ? round2(dowAcc[i].sum / dowAcc[i].count) : null,
  }));

  // ── Multi-week trajectory (only if ≥4 distinct ISO weeks of data) ────────
  let trajectory: Trajectory | null = null;
  const weeks = new Map<string, { sum: number; count: number }>();
  for (const { d, e } of dated) {
    const key = isoWeekKey(d);
    const cur = weeks.get(key) ?? { sum: 0, count: 0 };
    cur.sum += e.score;
    cur.count += 1;
    weeks.set(key, cur);
  }
  if (weeks.size >= 4) {
    let points: WeekPoint[] = [...weeks.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([iso_week, { sum, count }]) => ({ iso_week, avg_score: round2(sum / count), entries: count }));
    if (points.length > 8) points = points.slice(points.length - 8);
    // Least-squares slope of avg_score vs week index (0,1,2,…).
    const n = points.length;
    const mx = (n - 1) / 2;
    const my = points.reduce((s, pt) => s + pt.avg_score, 0) / n;
    let num = 0, den = 0;
    for (let i = 0; i < n; i++) {
      num += (i - mx) * (points[i].avg_score - my);
      den += (i - mx) ** 2;
    }
    const slope = den === 0 ? 0 : round2(num / den);
    trajectory = {
      weeks: points,
      slope_per_week: slope,
      direction: slope > 0.05 ? "rising" : slope < -0.05 ? "falling" : "flat",
    };
  }

  return {
    schema:
      "oriora.mood.v2-signals: derived aggregates only (no raw notes). All scores on a 1..5 scale where 1=rough, 5=great. Every figure is a plain observation — describe patterns/deltas, never advise or diagnose.",
    generated_for: fmtDay(today),
    overall,
    current_window,
    prior_window,
    window_shift,
    tag_correlations,
    day_of_week,
    volatility,
    trajectory,
  };
}
